import * as React from "react";
import { useForm, useFieldArray } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import {
  Button,
  DatePicker,
  FieldHint,
  Input,
  Label,
  useToast,
} from "@repo/ui";
import { formatToApiDate, cn } from "@repo/utils";
import { Calendar, List, PackagePlus, ShieldX, Truck } from "lucide-react";
import { Paperclip, Plus, Trash, X } from "@phosphor-icons/react";
import { SidePanel } from "@/components/side-panel";
import {
  InventorySearchCombobox,
  WholesalerCombobox,
  WholesalerManagementModal,
  CreateProductDialog,
} from "@/components/forms";
import { ApiClientError } from "@/lib/api/client";
import { getWholesalers } from "@/lib/api/wholesalers";
import { getLastInvoice, uploadReceiptInvoice } from "@/lib/api/receipts";
import {
  INVOICE_ACCEPT,
  formatFileSize,
  validateInvoiceFile,
} from "@/lib/validation/invoice-file";
import { useApproval } from "@/context/approval-context";
import type { ReceiptFormData } from "@/lib/types/inventory";
import type { InventoryItem } from "@/components/forms";
import type { Wholesaler } from "@/lib/types/inventory";

const API_URL = import.meta.env.VITE_API_URL || "http://localhost:3005";

// ─── Schema ──────────────────────────────────────────────────────────────────

const itemSchema = z.object({
  ndc: z
    .string()
    .min(1, "NDC is required")
    .regex(/^\d{5}-\d{4}-\d{2}$/, "Invalid NDC format (e.g., 12345-6789-01)"),
  itemName: z.string().min(1, "Item name is required"),
  quantityReceived: z.coerce.number().min(1, "Quantity must be ≥ 1"),
});

const receiptFormSchema = z
  .object({
    dateReceived: z.date({ required_error: "Date received is required" }),
    wholesalerId: z.string().min(1, "Wholesaler is required"),
    invoiceNumber: z.string().min(1, "Invoice number is required"),
    items: z.array(itemSchema).min(1),
  })
  .superRefine((data, ctx) => {
    const ndcs = data.items.map((i) => i.ndc);
    ndcs.forEach((ndc, idx) => {
      if (ndc && ndcs.indexOf(ndc) !== idx) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Duplicate NDC — already on this receipt",
          path: ["items", idx, "ndc"],
        });
      }
    });
  });

type ReceiptFormValues = z.infer<typeof receiptFormSchema>;

// ─── Per-row metadata (combobox display + loaded inventory) ──────────────────

interface RowMeta {
  inventory: InventoryItem | null;
  displayLabel: string;
}

function emptyRowMeta(): RowMeta {
  return { inventory: null, displayLabel: "" };
}

// ─── Props ───────────────────────────────────────────────────────────────────

/**
 * Prefill shape for the drawer. In create mode with prefill, only the first
 * line item is seeded (e.g. from the inventory "Log Receipt" row action). In
 * edit mode, this is the existing receipt being modified.
 */
export interface ReceiptFormInitialData {
  dateReceived?: Date;
  invoiceNumber?: string;
  wholesalerId?: string;
  ndc?: string;
  itemName?: string;
  quantityReceived?: number;
}

export interface ReceiptFormPanelProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  mode?: "create" | "edit";
  /**
   * Create mode — called per row; component loops internally. May return the
   * created receipt id so the panel can attach the invoice document to it.
   */
  onSubmitRow?: (
    data: ReceiptFormData,
    approvalToken?: string
  ) => Promise<string | void>;
  /** Edit mode — called once with the single row's data. */
  onUpdate?: (data: ReceiptFormData, approvalToken?: string) => Promise<void>;
  /** Create mode — called once after all rows finish (success or partial). */
  onAllSubmitted?: () => void;
  initialData?: ReceiptFormInitialData;
}

// ─── Component ───────────────────────────────────────────────────────────────

export function ReceiptFormPanel({
  open,
  onOpenChange,
  mode = "create",
  onSubmitRow,
  onUpdate,
  onAllSubmitted,
  initialData,
}: ReceiptFormPanelProps) {
  const { toast } = useToast();
  const { requestApproval } = useApproval();
  const isEditMode = mode === "edit";

  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [permissionError, setPermissionError] = React.useState<string | null>(
    null
  );
  const [isFetchingLastInvoice, setIsFetchingLastInvoice] =
    React.useState(false);
  const [rowMeta, setRowMeta] = React.useState<RowMeta[]>([emptyRowMeta()]);
  const [selectedWholesaler, setSelectedWholesaler] =
    React.useState<Wholesaler | null>(null);
  const [showManageWholesalers, setShowManageWholesalers] =
    React.useState(false);
  const [wholesalersRefreshTrigger, setWholesalersRefreshTrigger] =
    React.useState(0);
  const [invoiceFile, setInvoiceFile] = React.useState<File | null>(null);
  const [invoiceFileError, setInvoiceFileError] = React.useState<string | null>(
    null
  );
  const [isAttaching, setIsAttaching] = React.useState(false);
  const fileInputRef = React.useRef<HTMLInputElement | null>(null);
  const [showCreateProduct, setShowCreateProduct] = React.useState(false);
  const [createProductSearchQuery, setCreateProductSearchQuery] =
    React.useState("");
  const [createProductTargetRow, setCreateProductTargetRow] = React.useState(0);

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
    watch,
    control,
  } = useForm<ReceiptFormValues>({
    resolver: zodResolver(receiptFormSchema),
    defaultValues: {
      dateReceived: new Date(),
      invoiceNumber: "",
      wholesalerId: "",
      items: [{ ndc: "", itemName: "", quantityReceived: 0 }],
    },
  });

  const { fields, append, remove, replace } = useFieldArray({
    control,
    name: "items",
  });
  const dateReceived = watch("dateReceived");

  // ─── Reset / hydrate from initialData whenever the drawer opens ──────────
  React.useEffect(() => {
    if (!open) return;

    // Guard against late async results writing stale state after unmount
    // or when `initialData` changes rapidly (e.g. user opens edit on a
    // different receipt while the previous hydrate's fetch is in flight).
    let isCurrent = true;

    const hydrate = async () => {
      if (!isCurrent) return;
      setPermissionError(null);

      // No prefill — fresh blank create form.
      if (!initialData?.ndc) {
        if (!isCurrent) return;
        reset({
          dateReceived: new Date(),
          invoiceNumber: "",
          wholesalerId: "",
          items: [{ ndc: "", itemName: "", quantityReceived: 0 }],
        });
        setRowMeta([emptyRowMeta()]);
        setSelectedWholesaler(null);
        return;
      }

      // Prefill (create from inventory row, or edit existing receipt).
      if (!isCurrent) return;
      reset({
        dateReceived: initialData.dateReceived ?? new Date(),
        invoiceNumber: initialData.invoiceNumber ?? "",
        wholesalerId: initialData.wholesalerId ?? "",
        items: [
          {
            ndc: initialData.ndc ?? "",
            itemName: initialData.itemName ?? "",
            quantityReceived: initialData.quantityReceived ?? 0,
          },
        ],
      });

      const newMeta = emptyRowMeta();

      // Load inventory item for combobox display.
      try {
        const res = await fetch(
          `${API_URL}/inventory/search?q=${encodeURIComponent(initialData.ndc)}`,
          { credentials: "include" }
        );
        const data = await res.json();
        if (!isCurrent) return;
        const match = data.results?.find(
          (item: InventoryItem) => item.ndc === initialData.ndc
        );
        if (match) {
          newMeta.inventory = match;
          newMeta.displayLabel = match.label;
        } else {
          newMeta.displayLabel = `${initialData.ndc} - ${initialData.itemName ?? ""}`;
        }
      } catch {
        if (!isCurrent) return;
        newMeta.displayLabel = `${initialData.ndc} - ${initialData.itemName ?? ""}`;
      }

      // Load wholesaler for display.
      if (initialData.wholesalerId) {
        try {
          const data = await getWholesalers();
          if (!isCurrent) return;
          const wholesaler = data.data?.wholesalers?.find(
            (w: Wholesaler) => w.id === initialData.wholesalerId
          );
          if (wholesaler) {
            setSelectedWholesaler(wholesaler);
          }
        } catch {
          // leave null — user can pick again
        }
      } else {
        if (!isCurrent) return;
        setSelectedWholesaler(null);
      }

      if (!isCurrent) return;
      setRowMeta([newMeta]);
    };

    hydrate();

    return () => {
      isCurrent = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, initialData]);

  // ─── Row helpers ─────────────────────────────────────────────────────────

  const updateRowMeta = (index: number, patch: Partial<RowMeta>) => {
    setRowMeta((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], ...patch };
      return next;
    });
  };

  const handleAddRow = () => {
    append({ ndc: "", itemName: "", quantityReceived: 0 });
    setRowMeta((prev) => [...prev, emptyRowMeta()]);
  };

  const handleRemoveRow = (index: number) => {
    if (fields.length === 1) return;
    remove(index);
    setRowMeta((prev) => prev.filter((_, i) => i !== index));
  };

  const handleInventorySelect = (index: number, item: InventoryItem | null) => {
    if (item) {
      setValue(`items.${index}.ndc`, item.ndc, { shouldValidate: true });
      setValue(`items.${index}.itemName`, item.itemName, {
        shouldValidate: true,
      });
      updateRowMeta(index, { inventory: item, displayLabel: item.label });
    } else {
      setValue(`items.${index}.ndc`, "", { shouldValidate: false });
      setValue(`items.${index}.itemName`, "", { shouldValidate: false });
      updateRowMeta(index, { inventory: null, displayLabel: "" });
    }
  };

  const handleWholesalerSelect = (wholesaler: Wholesaler | null) => {
    if (wholesaler) {
      setSelectedWholesaler(wholesaler);
      setValue("wholesalerId", wholesaler.id, { shouldValidate: true });
    } else {
      setSelectedWholesaler(null);
      setValue("wholesalerId", "", { shouldValidate: false });
    }
  };

  const handleUseLastInvoice = async () => {
    setIsFetchingLastInvoice(true);
    try {
      const data = await getLastInvoice();
      if (data.data?.invoice_number) {
        setValue("invoiceNumber", data.data.invoice_number, {
          shouldValidate: true,
        });
      } else {
        toast({
          title: "No previous invoice",
          description: "No previous invoice number found.",
          variant: "destructive",
        });
      }
    } catch {
      toast({
        title: "Error",
        description: "Failed to fetch last invoice number.",
        variant: "destructive",
      });
    } finally {
      setIsFetchingLastInvoice(false);
    }
  };

  const handleInvoiceFileChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = event.target.files?.[0] ?? null;
    if (!file) return;
    const error = validateInvoiceFile(file);
    if (error) {
      setInvoiceFileError(error);
      setInvoiceFile(null);
    } else {
      setInvoiceFileError(null);
      setInvoiceFile(file);
    }
    // Allow re-selecting the same file after removal.
    event.target.value = "";
  };

  const handleRemoveInvoiceFile = () => {
    setInvoiceFile(null);
    setInvoiceFileError(null);
  };

  const handleClose = () => {
    reset({
      dateReceived: new Date(),
      invoiceNumber: "",
      wholesalerId: "",
      items: [{ ndc: "", itemName: "", quantityReceived: 0 }],
    });
    setRowMeta([emptyRowMeta()]);
    setSelectedWholesaler(null);
    setPermissionError(null);
    setInvoiceFile(null);
    setInvoiceFileError(null);
    onOpenChange(false);
  };

  // ─── Submit ──────────────────────────────────────────────────────────────

  /**
   * Build a ReceiptFormData payload for a single row out of the shared
   * header fields plus one item.
   */
  const buildRowPayload = (
    item: ReceiptFormValues["items"][number],
    dateString: string,
    wholesalerId: string,
    invoiceNumber: string
  ): ReceiptFormData =>
    ({
      date_received: dateString,
      ndc: item.ndc,
      quantity_received: item.quantityReceived,
      wholesaler_id: wholesalerId,
      invoice_number: invoiceNumber,
      item_name: item.itemName,
    }) as ReceiptFormData;

  /**
   * Turn a 403 with `requiresApproval` into an approval-modal request.
   * Returns true if the modal was opened (caller should pause); false if
   * no approval flow is available (caller should treat as denied).
   */
  const openApprovalForError = (
    err: ApiClientError,
    retryFn: (approvalToken: string) => Promise<void>,
    actionDescription: string
  ): boolean => {
    const errData = err.data as Record<string, unknown>;
    if (errData.requiresApproval !== true) return false;
    const fakeResponse = new Response(JSON.stringify(errData), {
      status: 403,
      headers: { "Content-Type": "application/json" },
    });
    return requestApproval(fakeResponse, errData, retryFn, {
      actionDescription,
    });
  };

  /**
   * Process a slice of items (from `startIndex`) in create mode. On a 403
   * that needs approval, opens the approval modal with a retryFn that
   * resumes this same function from the failing index with the new token,
   * carrying forward the success/failed accumulators so no items are lost.
   *
   * Failures are tracked by their index into the original `items` array so
   * that on partial success we can prune the already-submitted rows from
   * the form state — preventing duplicate resubmission if the user clicks
   * "Log Receipts" again.
   */
  const processCreateItems = async (
    items: ReceiptFormValues["items"],
    shared: {
      dateString: string;
      wholesalerId: string;
      invoiceNumber: string;
    },
    startIndex: number,
    approvalToken: string | undefined,
    priorFailedIndices: number[],
    priorCreatedIds: string[]
  ): Promise<void> => {
    if (!onSubmitRow) return;

    const failedIndices = [...priorFailedIndices];
    const createdIds = [...priorCreatedIds];
    let denied = false;

    for (let idx = startIndex; idx < items.length; idx++) {
      const item = items[idx];
      // Token only applies to the originally-failed item on resume.
      const tokenForThisItem = idx === startIndex ? approvalToken : undefined;

      try {
        const createdId = await onSubmitRow(
          buildRowPayload(
            item,
            shared.dateString,
            shared.wholesalerId,
            shared.invoiceNumber
          ),
          tokenForThisItem
        );
        if (typeof createdId === "string") createdIds.push(createdId);
      } catch (err) {
        if (err instanceof ApiClientError && err.statusCode === 403) {
          const errData = err.data as Record<string, unknown>;
          if (errData.requiresApproval === true) {
            const opened = openApprovalForError(
              err,
              async (token) => {
                // Re-lock the form while the approved retry is in flight
                // so the user can't interact with or re-submit the drawer.
                setIsSubmitting(true);
                await processCreateItems(
                  items,
                  shared,
                  idx,
                  token,
                  failedIndices,
                  createdIds
                );
              },
              "Log receipt"
            );
            if (opened) {
              // Pause submission — approval modal is open. The retryFn
              // will resume from this index and finalize when done.
              setIsSubmitting(false);
              return;
            }
          }
          // 403 denied (no approval flow available, or approval modal
          // refused to open): stop the loop and report partial results.
          setPermissionError(
            "You don't have permission to perform this action. Contact your administrator for access."
          );
          // Mark this item + any not-yet-processed items as unprocessed
          // so we don't lose them from the form.
          for (let j = idx; j < items.length; j++) {
            if (!failedIndices.includes(j)) failedIndices.push(j);
          }
          denied = true;
          break;
        }
        failedIndices.push(idx);
      }
    }

    // Loop finished (success, denied break, or non-403 errors). Attach the
    // invoice document to every created receipt BEFORE unlocking the drawer,
    // so the user sees an explicit "Attaching Document..." state instead of
    // a stale-looking form while the uploads are in flight.
    if (invoiceFile && createdIds.length > 0) {
      setIsAttaching(true);
      const results = await Promise.allSettled(
        createdIds.map((id) => uploadReceiptInvoice(id, invoiceFile))
      );
      setIsAttaching(false);
      const attachFailures = results.filter(
        (r) => r.status === "rejected"
      ).length;
      if (attachFailures > 0) {
        toast({
          title: "Document Not Attached",
          description: `Receipts were logged, but the invoice document failed to attach to ${attachFailures} item${attachFailures !== 1 ? "s" : ""}. You can attach it from the receipt details.`,
          variant: "destructive",
        });
      }
    }

    setIsSubmitting(false);

    const successCount = items.length - failedIndices.length;
    const failedLabels = failedIndices.map(
      (i) => items[i].itemName || items[i].ndc
    );

    if (failedIndices.length === 0) {
      // Full success.
      toast({
        title: "Receipts Logged",
        description: `${successCount} receipt${successCount !== 1 ? "s" : ""} logged successfully.`,
        variant: "success",
      });
      onAllSubmitted?.();
      handleClose();
      return;
    }

    if (successCount > 0) {
      // Partial success — show summary and prune succeeded rows from the
      // form so the user can fix and resubmit only the failures without
      // creating duplicates.
      toast({
        title: "Partial Success",
        description: denied
          ? `${successCount} logged. Remaining items were not processed.`
          : `${successCount} logged. Failed: ${failedLabels.join(", ")}.`,
        variant: "destructive",
      });

      const remainingItems = failedIndices.map((i) => items[i]);
      const failedIndexSet = new Set(failedIndices);
      replace(remainingItems);
      setRowMeta((prev) => {
        // Defensive: only filter if prev is in sync with the original
        // items array; otherwise rebuild blank meta for the remaining rows.
        if (prev.length !== items.length) {
          return remainingItems.map(() => emptyRowMeta());
        }
        return prev.filter((_, i) => failedIndexSet.has(i));
      });

      onAllSubmitted?.();
      return;
    }

    // No successes at all — leave the form intact and just toast.
    toast({
      title: "Error",
      description: "Failed to log receipts. Please try again.",
      variant: "destructive",
    });
  };

  const handleFormSubmit = async (data: ReceiptFormValues) => {
    setPermissionError(null);
    setIsSubmitting(true);
    const dateString = formatToApiDate(data.dateReceived);

    // Edit mode — single row update with approval-aware retry.
    if (isEditMode) {
      if (!onUpdate) {
        toast({
          title: "Error",
          description: "Missing update handler.",
          variant: "destructive",
        });
        setIsSubmitting(false);
        return;
      }

      const payload = buildRowPayload(
        data.items[0],
        dateString,
        data.wholesalerId,
        data.invoiceNumber
      );

      const runUpdate = async (token?: string) => {
        try {
          await onUpdate(payload, token);
          setIsSubmitting(false);
          handleClose();
        } catch (err) {
          if (err instanceof ApiClientError && err.statusCode === 403) {
            const opened = openApprovalForError(
              err,
              async (newToken) => {
                // Re-lock the form while the approved retry is in flight.
                setIsSubmitting(true);
                await runUpdate(newToken);
              },
              "Update receipt"
            );
            if (opened) {
              setIsSubmitting(false);
              return;
            }
            setPermissionError(
              "You don't have permission to perform this action. Contact your administrator for access."
            );
            setIsSubmitting(false);
            return;
          }
          // Non-permission errors: parent already surfaced a toast.
          setIsSubmitting(false);
        }
      };

      await runUpdate();
      return;
    }

    // Create mode — resumable loop through rows.
    if (!onSubmitRow) {
      toast({
        title: "Error",
        description: "Missing submit handler.",
        variant: "destructive",
      });
      setIsSubmitting(false);
      return;
    }

    await processCreateItems(
      data.items,
      {
        dateString,
        wholesalerId: data.wholesalerId,
        invoiceNumber: data.invoiceNumber,
      },
      0,
      undefined,
      [],
      []
    );
  };

  // ─── Render ──────────────────────────────────────────────────────────────

  const itemErrors = errors.items;
  const multiRow = fields.length > 1;

  return (
    <SidePanel
      open={open}
      onOpenChange={(next) => {
        if (!next) handleClose();
        else onOpenChange(true);
      }}
      icon={PackagePlus}
      title={isEditMode ? "Edit Receipt" : "Log Receipt"}
      description={
        isEditMode
          ? "Update receipt information"
          : "Log incoming inventory from wholesaler"
      }
      maxWidthClassName="sm:max-w-2xl"
      footer={
        <div className="flex items-center justify-between gap-3">
          {!isEditMode ? (
            <span className="text-xs text-muted-foreground tabular-nums">
              {fields.length} drug{fields.length !== 1 ? "s" : ""} to submit
            </span>
          ) : (
            <span />
          )}
          <div className="flex gap-3">
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              form="receipt-form"
              disabled={isSubmitting}
              data-testid="receipt-form-submit"
            >
              {isSubmitting
                ? isAttaching
                  ? "Attaching Document..."
                  : isEditMode
                    ? "Updating..."
                    : "Logging..."
                : isEditMode
                  ? "Update Receipt"
                  : multiRow
                    ? `Log ${fields.length} Receipts`
                    : "Log Receipt"}
            </Button>
          </div>
        </div>
      }
    >
      <form
        id="receipt-form"
        onSubmit={handleSubmit(handleFormSubmit)}
        className="space-y-5"
      >
        {permissionError && (
          <div className="flex items-start gap-3 rounded-lg border border-destructive/30 bg-destructive/5 px-4 py-3 text-sm">
            <ShieldX className="h-4 w-4 shrink-0 mt-0.5 text-destructive" />
            <div>
              <p className="font-medium text-destructive">Permission Denied</p>
              <p className="text-destructive/80 mt-0.5 text-xs leading-relaxed">
                {permissionError}
              </p>
            </div>
          </div>
        )}

        {/* ── Section: When ─────────────────────────────────────────── */}
        <fieldset className="space-y-3">
          <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
            <Calendar className="h-3.5 w-3.5" />
            When
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="dateReceived" className="text-xs">
              Date Received
            </Label>
            <DatePicker
              date={dateReceived}
              onDateChange={(date) =>
                setValue("dateReceived", date || new Date(), {
                  shouldValidate: true,
                })
              }
              placeholder="Select date"
            />
            {errors.dateReceived && (
              <p className="text-xs text-destructive">
                {errors.dateReceived.message}
              </p>
            )}
          </div>
        </fieldset>

        <div className="border-t border-border/50" />

        {/* ── Section: Source ───────────────────────────────────────── */}
        <fieldset className="space-y-3">
          <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
            <Truck className="h-3.5 w-3.5" />
            Source
          </div>

          <div className="space-y-1.5">
            <div className="flex items-center gap-1.5">
              <Label htmlFor="wholesaler" className="text-xs">
                Wholesaler
              </Label>
              <FieldHint>
                The supplier you bought this medication from, for example
                Cardinal or McKesson. You can also create new wholesalers from
                this field.
              </FieldHint>
            </div>
            <WholesalerCombobox
              value={selectedWholesaler?.id}
              onSelect={handleWholesalerSelect}
              placeholder="Select or create wholesaler..."
              searchPlaceholder="Search or type to create..."
              disabled={isSubmitting}
              displayLabel={selectedWholesaler?.name}
              onManageClick={() => setShowManageWholesalers(true)}
              refreshTrigger={wholesalersRefreshTrigger}
              data-testid="receipt-form-wholesaler"
            />
            {errors.wholesalerId && (
              <p className="text-xs text-destructive">
                {errors.wholesalerId.message}
              </p>
            )}
          </div>

          <div className="space-y-1.5">
            <div className="flex items-center justify-between">
              <Label htmlFor="invoiceNumber" className="text-xs">
                Invoice Number
              </Label>
              {!isEditMode && (
                <button
                  type="button"
                  onClick={handleUseLastInvoice}
                  disabled={isFetchingLastInvoice || isSubmitting}
                  className="text-[11px] font-medium text-primary hover:underline disabled:opacity-50"
                >
                  {isFetchingLastInvoice ? "Loading..." : "Use last invoice #"}
                </button>
              )}
            </div>
            <Input
              id="invoiceNumber"
              placeholder="Enter invoice number"
              data-testid="receipt-form-invoice"
              {...register("invoiceNumber")}
            />
            {errors.invoiceNumber && (
              <p className="text-xs text-destructive">
                {errors.invoiceNumber.message}
              </p>
            )}
          </div>

          {!isEditMode && (
            <div className="space-y-1.5">
              <div className="flex items-center gap-1.5">
                <Label htmlFor="invoiceDocument" className="text-xs">
                  Invoice Document{" "}
                  <span className="font-normal text-muted-foreground">
                    (optional)
                  </span>
                </Label>
                <FieldHint>
                  Attach a scanned copy or photo of the wholesaler invoice for
                  your records. PDF, JPG, PNG, or WebP up to 10 MB. It will be
                  saved on every item in this receipt.
                </FieldHint>
              </div>
              {invoiceFile ? (
                <div className="flex items-center justify-between gap-2 rounded-lg border border-border/60 bg-surface px-3 py-2">
                  <div className="flex min-w-0 items-center gap-2">
                    <Paperclip className="h-4 w-4 shrink-0 text-muted-foreground" />
                    <span className="truncate text-sm">{invoiceFile.name}</span>
                    <span className="shrink-0 text-xs text-muted-foreground tabular-nums">
                      {formatFileSize(invoiceFile.size)}
                    </span>
                  </div>
                  <button
                    type="button"
                    onClick={handleRemoveInvoiceFile}
                    disabled={isSubmitting}
                    className="text-muted-foreground hover:text-destructive disabled:opacity-30 transition-colors"
                    aria-label="Remove invoice document"
                  >
                    <X className="h-4 w-4" weight="bold" />
                  </button>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={isSubmitting}
                  data-testid="receipt-form-invoice-file"
                  className="flex w-full items-center justify-center gap-1.5 rounded-lg border border-dashed border-border bg-transparent py-2.5 text-xs font-medium text-primary transition-colors hover:border-primary/60 hover:bg-primary-soft/50 disabled:opacity-50"
                >
                  <Paperclip className="h-3.5 w-3.5" weight="bold" />
                  Attach Invoice Document
                </button>
              )}
              <input
                ref={fileInputRef}
                id="invoiceDocument"
                type="file"
                accept={INVOICE_ACCEPT}
                className="hidden"
                onChange={handleInvoiceFileChange}
              />
              {invoiceFileError && (
                <p className="text-xs text-destructive">{invoiceFileError}</p>
              )}
            </div>
          )}
        </fieldset>

        <div className="border-t border-border/50" />

        {/* ── Section: Items ────────────────────────────────────────── */}
        <fieldset className="space-y-3">
          <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
            <List className="h-3.5 w-3.5" />
            {isEditMode ? "Medication" : "Items"}
          </div>

          <div className="space-y-2.5">
            {fields.map((field, index) => {
              const meta = rowMeta[index] ?? emptyRowMeta();
              const rowErrors = Array.isArray(itemErrors)
                ? itemErrors[index]
                : undefined;
              const hasError =
                rowErrors?.ndc ||
                rowErrors?.itemName ||
                rowErrors?.quantityReceived;

              return (
                <div
                  key={field.id}
                  className={cn(
                    "rounded-lg border bg-surface p-3 transition-colors",
                    hasError
                      ? "border-destructive/40 bg-destructive/5"
                      : "border-border/60"
                  )}
                >
                  {!isEditMode && multiRow && (
                    <div className="mb-2 flex items-center justify-between">
                      <span className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                        Item {index + 1}
                      </span>
                      <button
                        type="button"
                        onClick={() => handleRemoveRow(index)}
                        disabled={isSubmitting}
                        className="inline-flex items-center gap-1 text-[11px] font-medium text-muted-foreground hover:text-destructive disabled:opacity-30 transition-colors"
                        aria-label={`Remove item ${index + 1}`}
                      >
                        <Trash className="h-3 w-3" weight="bold" />
                        Remove
                      </button>
                    </div>
                  )}

                  <div className="grid grid-cols-[1fr_6.5rem] gap-2.5">
                    <div className="min-w-0 space-y-1">
                      <Label
                        htmlFor={`items.${index}.ndc`}
                        className="text-[11px] text-muted-foreground"
                      >
                        Drug
                      </Label>
                      <InventorySearchCombobox
                        value={meta.inventory?.id}
                        valueItem={meta.inventory}
                        onSelect={(item) => handleInventorySelect(index, item)}
                        placeholder="Search NDC or name..."
                        searchPlaceholder="Type to search inventory..."
                        disabled={isSubmitting}
                        displayLabel={meta.displayLabel}
                        autoOpen={!isEditMode && !multiRow && !initialData?.ndc}
                        onCreateNew={(query) => {
                          setCreateProductSearchQuery(query);
                          setCreateProductTargetRow(index);
                          setShowCreateProduct(true);
                        }}
                        data-testid={`receipt-form-ndc-${index}`}
                      />
                      {(rowErrors?.ndc || rowErrors?.itemName) && (
                        <p className="text-xs text-destructive">
                          {rowErrors.ndc?.message ||
                            rowErrors.itemName?.message}
                        </p>
                      )}
                    </div>

                    <div className="space-y-1">
                      <Label
                        htmlFor={`items.${index}.quantityReceived`}
                        className="text-[11px] text-muted-foreground"
                      >
                        Qty of units
                      </Label>
                      <Input
                        id={`items.${index}.quantityReceived`}
                        type="number"
                        min={1}
                        placeholder="0"
                        className="h-9"
                        data-testid={`receipt-form-qty-${index}`}
                        {...register(`items.${index}.quantityReceived`)}
                      />
                      {rowErrors?.quantityReceived && (
                        <p className="text-xs text-destructive">
                          {rowErrors.quantityReceived.message}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {!isEditMode && (
            <button
              type="button"
              onClick={handleAddRow}
              disabled={isSubmitting}
              className="flex w-full items-center justify-center gap-1.5 rounded-lg border border-dashed border-border bg-transparent py-2.5 text-xs font-medium text-primary transition-colors hover:border-primary/60 hover:bg-primary-soft/50 disabled:opacity-50"
            >
              <Plus className="h-3.5 w-3.5" weight="bold" />
              Add Another Item
            </button>
          )}
        </fieldset>
      </form>

      {/* Wholesaler Management Modal */}
      <WholesalerManagementModal
        open={showManageWholesalers}
        onOpenChange={setShowManageWholesalers}
        onWholesalersChange={() => {
          setWholesalersRefreshTrigger((prev) => prev + 1);
        }}
      />

      {/* Create Product Dialog */}
      <CreateProductDialog
        open={showCreateProduct}
        onOpenChange={setShowCreateProduct}
        initialSearchQuery={createProductSearchQuery}
        onProductCreated={(item) => {
          handleInventorySelect(createProductTargetRow, item);
        }}
      />
    </SidePanel>
  );
}
